/**
 * Allowlist HTML sanitizer for content rendered with dangerouslySetInnerHTML (security finding F08).
 *
 * Merchant-authored rich text (item/donation descriptions, policies) and uploaded SVG logos are shown on
 * PUBLIC checkout, purchase-link and fundraiser pages. Without sanitizing, a <script>, an onerror=... or a
 * javascript: link in that content would run in every visitor's browser.
 *
 * Uses the browser's own DOMParser (no extra dependency). Keeps the formatting the editors produce
 * (paragraphs, inline styles, lists, links, images, tables) and plain SVG drawing elements; removes
 * scripts, embedded documents, forms, event handlers (on*) and unsafe URLs. Unknown tags are unwrapped
 * (their text stays). Outside a browser it returns the text HTML-escaped, so it never fails open.
 */

const ALLOWED_TAGS = new Set([
  // text / layout produced by the rich-text editors
  "p", "br", "b", "strong", "i", "em", "u", "s", "strike", "del", "ins", "span", "div", "small", "mark",
  "font", "sub", "sup", "code", "pre", "blockquote", "hr", "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li", "a", "img", "figure", "figcaption",
  "table", "thead", "tbody", "tfoot", "tr", "td", "th", "caption", "colgroup", "col",
  // SVG drawing (logos)
  "svg", "g", "path", "circle", "ellipse", "line", "polyline", "polygon", "rect", "defs",
  "lineargradient", "radialgradient", "stop", "clippath", "mask", "title", "desc", "text", "tspan",
]);

/** Removed together with everything inside them. */
const DROPPED_TAGS = new Set([
  "script", "style", "iframe", "frame", "frameset", "object", "embed", "applet", "link", "meta", "base",
  "form", "input", "button", "textarea", "select", "option", "noscript", "template", "foreignobject",
  "use", "image", "animate", "set", "animatetransform", "animatemotion", "math",
]);

const ALLOWED_ATTRS = new Set([
  "style", "class", "title", "dir", "align", "lang",
  "href", "target", "rel", "name",
  "src", "alt", "width", "height",
  "colspan", "rowspan", "color", "size", "face",
  // SVG
  "xmlns", "viewbox", "fill", "fill-rule", "fill-opacity", "clip-rule", "stroke", "stroke-width",
  "stroke-linecap", "stroke-linejoin", "stroke-miterlimit", "stroke-dasharray", "stroke-dashoffset",
  "stroke-opacity", "opacity", "d", "cx", "cy", "r", "rx", "ry", "x", "y", "x1", "y1", "x2", "y2",
  "points", "transform", "offset", "stop-color", "stop-opacity", "gradientunits", "gradienttransform",
  "preserveaspectratio", "version", "clip-path", "mask", "font-size", "font-family", "font-weight",
  "text-anchor", "dominant-baseline", "id",
]);

const URL_ATTRS = new Set(["href", "src"]);
const SAFE_URL = /^(https?:|mailto:|tel:|#|\/(?!\/)|[^:]*$)/i;
const SAFE_IMAGE_DATA_URL = /^data:image\/(png|jpe?g|gif|webp);base64,/i;
const UNSAFE_STYLE = /expression\s*\(|javascript:|vbscript:|behavior\s*:|-moz-binding|@import/i;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function cleanAttributes(el: Element) {
  const tag = el.tagName.toLowerCase();
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    const value = attr.value.trim();

    // "id" only inside SVG (gradients/clip paths reference it); in HTML it could clobber page globals.
    const allowed = ALLOWED_ATTRS.has(name) && !(name === "id" && !(el instanceof SVGElement));
    if (!allowed || name.startsWith("on")) {
      el.removeAttribute(attr.name);
      continue;
    }
    if (URL_ATTRS.has(name)) {
      const compact = value.replace(/[\u0000- ]/g, "");
      const ok = SAFE_URL.test(compact) || (tag === "img" && name === "src" && SAFE_IMAGE_DATA_URL.test(compact));
      if (!ok) el.removeAttribute(attr.name);
      continue;
    }
    if (name === "style" && UNSAFE_STYLE.test(value)) {
      el.removeAttribute(attr.name);
      continue;
    }
    // SVG presentation attributes may hold url(...) references; only same-document ones (#id) are kept.
    if (/url\s*\(/i.test(value) && !/url\s*\(\s*['"]?#/i.test(value)) {
      el.removeAttribute(attr.name);
    }
  }

  if (tag === "a" && el.getAttribute("target") === "_blank") {
    el.setAttribute("rel", "noopener noreferrer");
  }
}

function cleanNode(node: Node) {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.COMMENT_NODE) {
      child.remove();
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;

    const el = child as Element;
    const tag = el.tagName.toLowerCase();
    if (DROPPED_TAGS.has(tag)) {
      el.remove();
      continue;
    }
    cleanNode(el);
    if (!ALLOWED_TAGS.has(tag)) {
      // Unknown tag: keep its (already cleaned) children, drop the element itself.
      el.replaceWith(...Array.from(el.childNodes));
      continue;
    }
    cleanAttributes(el);
  }
}

export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return "";
  if (typeof DOMParser === "undefined") {
    return escapeHtml(String(html));
  }
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${html}</body>`, "text/html");
  cleanNode(doc.body);
  return doc.body.innerHTML;
}
